import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, BarChart3, Star, Users, PlayCircle, FileText,
  ChevronRight, Check, Briefcase, Heart, Loader2,
  CheckCircle2, XCircle, AlertCircle, Info, X,
  Zap, Sun, Tag, BadgePercent
} from 'lucide-react';
import { Button }   from '@/components/ui/button';
import { Badge }    from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { UserContext } from '@/lib/User';
import SummerBanner from '@/lib/SummerBanner';

const API_BASE = 'https://xceededge.com';

// ─────────────────────────────────────────────────────────────────────────────
// TOAST SYSTEM
// ─────────────────────────────────────────────────────────────────────────────

const TOAST_ICONS = {
  success: <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />,
  error:   <XCircle     className="w-4 h-4 text-red-500    shrink-0" />,
  warning: <AlertCircle className="w-4 h-4 text-amber-500  shrink-0" />,
  info:    <Info        className="w-4 h-4 text-blue-500   shrink-0" />,
};

const TOAST_STYLES = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  error:   'border-red-200    bg-red-50    text-red-900',
  warning: 'border-amber-200  bg-amber-50  text-amber-900',
  info:    'border-blue-200   bg-blue-50   text-blue-900',
};

let _setToasts = null;
let _idCounter = 0;

export const toast = {
  show: (message, type = 'info', duration = 3500) => {
    if (!_setToasts) return;
    const id = ++_idCounter;
    _setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => _setToasts(prev => prev.filter(t => t.id !== id)), duration);
  },
  success: (msg, dur) => toast.show(msg, 'success', dur),
  error:   (msg, dur) => toast.show(msg, 'error',   dur),
  warning: (msg, dur) => toast.show(msg, 'warning', dur),
  info:    (msg, dur) => toast.show(msg, 'info',    dur),
};

function ToastContainer() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => { _setToasts = setToasts; return () => { _setToasts = null; }; }, []);
  const dismiss = (id) => setToasts(prev => prev.filter(t => t.id !== id));
  return (
    <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2 w-80">
      <AnimatePresence>
        {toasts.map(t => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, x: 60, scale: 0.95 }}
            animate={{ opacity: 1, x: 0,  scale: 1    }}
            exit={{    opacity: 0, x: 60, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg text-sm font-medium ${TOAST_STYLES[t.type]}`}
          >
            {TOAST_ICONS[t.type]}
            <span className="flex-1 leading-snug">{t.message}</span>
            <button onClick={() => dismiss(t.id)} className="opacity-50 hover:opacity-100 transition-opacity mt-0.5">
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FEE BREAKDOWN — now coupon-aware
// ─────────────────────────────────────────────────────────────────────────────

function FeeBreakdown({ course, couponDiscount, couponLabel }) {
  const original = Number(course.original_price);
  const listed   = Number(course.price);
  const saleSave = original - listed;
  const salePct  = Math.round((saleSave / original) * 100);
  const finalAmt = Math.max(0, listed - (couponDiscount || 0));

  const fmt = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

  return (
    <div className="rounded-xl border border-dashed border-muted-foreground/30 bg-muted/30 p-4 space-y-2.5 text-sm">
      <p className="font-semibold text-foreground text-xs uppercase tracking-wide mb-1">Fee Breakdown</p>

      <div className="flex justify-between text-muted-foreground">
        <span>Course MRP</span>
        <span className="line-through">{fmt(original)}</span>
      </div>

      <div className="flex justify-between text-emerald-600 font-medium">
        <span>Xceed Summer Discount ({salePct}%)</span>
        <span>− {fmt(saleSave)}</span>
      </div>

      {couponDiscount > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="flex justify-between text-violet-600 font-medium"
        >
          <span className="flex items-center gap-1.5">
            <Tag className="w-3 h-3" />
            Coupon ({couponLabel})
          </span>
          <span>− {fmt(couponDiscount)}</span>
        </motion.div>
      )}

      <div className="border-t border-foreground/10 pt-2.5 flex justify-between font-bold text-base text-foreground">
        <span>Total Payable</span>
        <span>{fmt(finalAmt)}</span>
      </div>

      <p className="text-[11px] text-muted-foreground">* GST included. No hidden charges.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COUPON INPUT
// ─────────────────────────────────────────────────────────────────────────────

function CouponInput({ courseId, onApply, onRemove, applied, user }) {
  const [code,      setCode]      = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  const handleApply = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) { setError('Enter a coupon code'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API_BASE}/validate-coupon.php`,
        { coupon_code: trimmed, course_id: courseId,session:user?.session || '' },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data.valid) {
        onApply({ code: trimmed, discount: data.discount, label: data.label, message: data.message });
      } else {
        setError(data.error || 'Invalid coupon');
      }
    } catch {
      setError('Could not validate coupon. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setCode('');
    setError('');
    onRemove();
  };

  // ── Applied state ──────────────────────────────────────────────────────────
  if (applied) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1,  scale: 1    }}
        className="flex items-center justify-between gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <BadgePercent className="w-4 h-4 text-violet-600 shrink-0" />
          <div>
            <p className="text-xs font-bold text-violet-700 uppercase tracking-wide">{applied.code}</p>
            <p className="text-xs text-violet-600">{applied.message}</p>
          </div>
        </div>
        <button
          onClick={handleRemove}
          className="text-violet-400 hover:text-violet-700 transition-colors shrink-0"
          title="Remove coupon"
        >
          <X className="w-4 h-4" />
        </button>
      </motion.div>
    );
  }

  // ── Input state ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="text"
            value={code}
            onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleApply()}
            placeholder="Enter coupon code"
            className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 uppercase tracking-widest placeholder:normal-case placeholder:tracking-normal"
          />
        </div>
        <Button
          onClick={handleApply}
          disabled={loading || !code.trim()}
          variant="outline"
          className="shrink-0 border-primary text-primary hover:bg-primary hover:text-white transition-colors px-4"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
        </Button>
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1,  y: 0  }}
          className="text-xs text-red-500 flex items-center gap-1"
        >
          <XCircle className="w-3 h-3" /> {error}
        </motion.p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RAZORPAY LOADER
// ─────────────────────────────────────────────────────────────────────────────

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) return resolve(true);
    const script = document.createElement('script');
    script.id  = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────

export default function CourseDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [course,      setCourse]      = useState(null);
  const [enrolled,    setEnrolled]    = useState(false);
  const [wishlisted,  setWishlisted]  = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [enrolling,   setEnrolling]   = useState(false);
  const [wishlisting, setWishlisting] = useState(false);
  const [error,       setError]       = useState('');

  // Coupon state — null = no coupon applied
  // { code, discount, label, message } when applied
  const [coupon, setCoupon] = useState(null);

  // ── Fetch course + status ──────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setError('');

    const courseReq = axios.get(`${API_BASE}/course-detail.php?id=${id}`);
    const statusReq = user
      ? (() => {
          const fd = new FormData();
          fd.append('action',    'status');
          fd.append('course_id', id);
          fd.append('user_id',   user.id);
          return axios.post(`${API_BASE}/course-detail.php`, fd);
        })()
      : Promise.resolve({ data: { enrolled: false, wishlisted: false } });

    Promise.all([courseReq, statusReq])
      .then(([courseRes, statusRes]) => {
        if (courseRes.data.success) setCourse(courseRes.data.data);
        else setError('Course not found.');
        setEnrolled(statusRes.data.enrolled);
        setWishlisted(statusRes.data.wishlisted);
      })
      .catch(() => {
        setError('Failed to load course. Please try again.');
        toast.error('Failed to load course. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  // ── Enroll via Razorpay ────────────────────────────────────────────────────
  const handleEnroll = async () => {
    if (!user) {
      toast.warning('Please log in to enroll in this course.');
      navigate('/auth');
      return;
    }

    setEnrolling(true);

    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Failed to load payment gateway. Check your connection.');
        setEnrolling(false);
        return;
      }

      toast.info('Preparing your secure checkout…');

      const { data: order } = await axios.post(
        `${API_BASE}/create-order.php`,
        {
          session:      user.session,
          course_id:    id,
          coupon_code:  coupon?.code || '',   // ← pass applied coupon
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (order.error) {
        toast.error(order.error);
        setEnrolling(false);
        return;
      }

      const options = {
        key:         order.razorpay_key,
        amount:      order.amount,
        currency:    order.currency,
        name:        'XceedEdge',
        description: order.course_name,
        order_id:    order.order_id,

        prefill: {
          name:    user.full_name    || '',
          email:   user.email        || '',
          contact: user.phone_number || '',
        },

        theme: { color: '#ffb400' },

        handler: async (response) => {
          toast.info('Verifying your payment…');
          try {
            const { data: result } = await axios.post(
              `${API_BASE}/verify-payment.php`,
              {
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
                session:             user?.session,
                course_id:           id,
              },
              { headers: { 'Content-Type': 'application/json' } }
            );

            if (result.enrolled) {
              setEnrolled(true);
              toast.success('🎉 Enrollment successful! Redirecting to course…');
              setTimeout(() => navigate(`/player/${id}`), 1200);
            } else {
              toast.error(result.error || 'Payment verification failed. Contact support.');
            }
          } catch {
            toast.error('Verification request failed. Please contact support.');
          } finally {
            setEnrolling(false);
          }
        },

        modal: {
          ondismiss: () => {
            toast.warning('Payment cancelled. You can retry anytime.');
            setEnrolling(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setEnrolling(false);
      });
      rzp.open();

    } catch {
      toast.error('Something went wrong. Please try again.');
      setEnrolling(false);
    }
  };

  // ── Wishlist toggle ────────────────────────────────────────────────────────
  const handleWishlist = () => {
    if (!user) {
      toast.warning('Please log in to manage your wishlist.');
      navigate('/auth');
      return;
    }

    setWishlisting(true);
    const fd = new FormData();
    fd.append('action',    'wishlist');
    fd.append('course_id', id);
    fd.append('user_id',   user.id);

    axios.post(`${API_BASE}/course-detail.php`, fd)
      .then(res => {
        if (res.data.success) {
          setWishlisted(res.data.wishlisted);
          toast.success(res.data.wishlisted ? '❤️ Added to wishlist!' : 'Removed from wishlist.');
        } else {
          toast.error('Wishlist update failed. Try again.');
        }
      })
      .catch(() => toast.error('Wishlist update failed. Try again.'))
      .finally(() => setWishlisting(false));
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <>
      <ToastContainer />
      <div className="pt-32 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    </>
  );

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error || !course) return (
    <>
      <ToastContainer />
      <div className="pt-32 text-center">
        <h1 className="font-heading text-2xl font-bold">{error || 'Course not found'}</h1>
        <Link to="/courses" className="text-primary mt-4 inline-block">← Back to courses</Link>
      </div>
    </>
  );

  // lessons may be an array (new format) or a comma-separated string (legacy)
  const lessonsArrayOf = (m) =>
    Array.isArray(m.lessons)
      ? m.lessons
      : String(m.lessons || '').split(',').map(s => s.trim()).filter(Boolean);

  const totalLessons = course.curriculum.reduce((sum, m) => sum + lessonsArrayOf(m).length, 0);
  const finalPrice   = Math.max(0, Number(course.price) - (coupon?.discount || 0));

  return (
    <>
      <ToastContainer />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/courses" className="hover:text-primary">Courses</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground truncate">{course.title}</span>
          </div>

          {/* Summer Banner */}
          <SummerBanner course={course} />

          <div className="flex flex-col-reverse lg:grid lg:grid-cols-3 gap-8">

            {/* ── Main Content ── */}
            <div className="lg:col-span-2 space-y-8">

              {/* Header */}
              <div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {course.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold">{course.title}</h1>
                <p className="text-muted-foreground mt-3 text-lg leading-relaxed">{course.description}</p>

                <div className="flex flex-wrap items-center gap-6 mt-6">
                  <div className="flex items-center gap-1.5">
                    <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                    <span className="font-semibold">{course.rating}</span>
                    <span className="text-muted-foreground text-sm">({course.review_count} reviews)</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Users className="w-4 h-4" /> {Number(course.students).toLocaleString()} students
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" /> {course.duration}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <BarChart3 className="w-4 h-4" /> {course.level}
                  </div>
                </div>
              </div>

              {/* Instructor */}
              <div className="bg-card rounded-xl border p-5 flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  {course.trainer_image ? (
                    <img
                      src={course.trainer_image}
                      alt={course.instructor_name}
                      className="w-14 h-14 object-cover rounded-full border border-primary/30 shadow"
                    />
                  ) : (
                    <img
                      src={'https://xceededge.com/data/images/profile.png'}
                      alt={course.instructor_name}
                      className="w-14 h-14 object-cover rounded-full border border-primary/30 shadow"
                    />
                  )}
                </div>
                <div>
                  <p className="font-semibold">{course.instructor_name}</p>
                  <p className="text-sm text-muted-foreground">{course.instructor_title}</p>
                </div>
              </div>

              {/* Curriculum */}
              <div>
                <h2 className="font-heading text-2xl font-bold mb-4">Curriculum</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  {course.curriculum.length} modules · {totalLessons} lessons
                </p>
                <Accordion type="single" collapsible className="space-y-2">
                  {course.curriculum.map((module, i) => (
                    <AccordionItem key={i} value={`module-${i}`} className="bg-card rounded-lg border px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3 text-left">
                          <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                            {i + 1}
                          </span>
                          <div>
                            <p className="font-medium text-sm">{module.module}</p>
                            <p className="text-xs text-muted-foreground">
                              {lessonsArrayOf(module).length} lessons · {module.duration}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-2 pb-2">
                          {lessonsArrayOf(module).map((lesson, j) => (
                            <div key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <PlayCircle className="w-4 h-4 text-primary/50" />
                              {lesson}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Projects */}
              <div>
                <h2 className="font-heading text-2xl font-bold mb-4">Projects You'll Build</h2>
                <div className="grid sm:grid-cols-3 gap-4">
                  {course.projects.map((project, i) => {
                    // Tolerate both shapes: legacy plain strings and new {title, description} objects
                    // (also handle legacy capitalised keys Title/Description and `desc` shorthand).
                    const title = typeof project === 'string'
                      ? project
                      : (project?.title ?? project?.Title ?? '');
                    const description = typeof project === 'string'
                      ? ''
                      : (project?.description ?? project?.desc ?? project?.Description ?? '');
                    return (
                      <div key={i} className="bg-card rounded-lg border p-4">
                        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center mb-3">
                          <FileText className="w-5 h-5 text-accent" />
                        </div>
                        <p className="font-medium text-sm">{title}</p>
                        {description && (
                          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Career Outcomes */}
              <div>
                <h2 className="font-heading text-2xl font-bold mb-4">Career Outcomes</h2>
                <div className="flex flex-wrap gap-3">
                  {course.career_outcomes.map((outcome, i) => (
                    <div key={i} className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                      <Briefcase className="w-4 h-4" />
                      <span className="text-sm font-medium">{outcome}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reviews */}
              <div>
                <h2 className="font-heading text-2xl font-bold mb-4">Student Reviews</h2>
                {course.reviews.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 px-4 bg-muted/40 rounded-xl border border-dashed border-muted-foreground/30">
                    <Star className="w-10 h-10 text-amber-300 mb-2 animate-bounce" />
                    <p className="font-heading text-lg font-semibold text-foreground mb-1">No Reviews Yet</p>
                    <p className="text-muted-foreground text-sm mb-2 text-center max-w-xs">Be the first to share your experience and help others choose the right course!</p>
                    <div className="flex gap-1 opacity-60">
                      <Star className="w-4 h-4" />
                      <Star className="w-4 h-4" />
                      <Star className="w-4 h-4" />
                      <Star className="w-4 h-4" />
                      <Star className="w-4 h-4" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {course.reviews.map((review, i) => (
                      <div key={i} className="bg-card rounded-lg border p-5">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                              {review.name[0]}
                            </div>
                            <span className="font-medium text-sm">{review.name}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{review.date}</span>
                        </div>
                        <div className="flex gap-0.5 mb-2">
                          {Array.from({ length: Number(review.rating) }).map((_, j) => (
                            <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                          ))}
                        </div>
                        <p className="text-sm text-muted-foreground">{review.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Pricing Card ── */}
            <div>
              <div className="sticky top-24 bg-card rounded-xl border shadow-lg overflow-hidden">
                <div className="aspect-video bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                  <PlayCircle className="w-16 h-16 text-primary/40" />
                </div>

                <div className="p-6 space-y-4">

                  {/* Price row — updates live with coupon */}
                  <div className="flex items-baseline gap-3">
                    <motion.span
                      key={finalPrice}
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1,   opacity: 1 }}
                      className="font-heading text-3xl font-bold"
                    >
                      ₹{finalPrice.toLocaleString('en-IN')}
                    </motion.span>
                    <span className="text-red-500 line-through text-sm">
                      ₹{Number(course.original_price).toLocaleString('en-IN')}
                    </span>
                    <Badge className="bg-accent text-accent-foreground">
                      {Math.round((1 - finalPrice / Number(course.original_price)) * 100)}% OFF
                    </Badge>
                  </div>

                  {/* Fee Breakdown */}
                  <FeeBreakdown
                    course={course}
                    couponDiscount={coupon?.discount || 0}
                    couponLabel={coupon?.label || ''}
                  />

                  {/* ── Coupon Input ── */}
                  <CouponInput
                    courseId={id}
                    applied={coupon}
                    user ={user}
                    onApply={(data) => {
                      setCoupon(data);
                      toast.success(`🎉 ${data.message}`);
                    }}
                    onRemove={() => {
                      setCoupon(null);
                      toast.info('Coupon removed.');
                    }}
                  />

                  {/* Summer mini-badge */}
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-700 text-xs font-medium">
                    <Sun className="w-3.5 h-3.5 shrink-0" />
                    Xceed Summer Offer price — grab it before it's gone!
                  </div>

                  {/* CTA */}
                  {enrolled ? (
                    <Link to={`/player/${course.id}`} className="block">
                      <Button className="w-full bg-green-600 hover:bg-green-700 h-12 text-base gap-2">
                        <PlayCircle className="w-5 h-5" /> Continue Learning
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      onClick={handleEnroll}
                      disabled={enrolling}
                      className="w-full bg-primary hover:bg-primary/90 h-12 text-base gap-2"
                    >
                      {enrolling
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
                        : <>
                            <Zap className="w-4 h-4" />
                            Enroll Now · ₹{finalPrice.toLocaleString('en-IN')}
                          </>
                      }
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    disabled={wishlisting}
                    onClick={handleWishlist}
                  >
                    {wishlisting
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Heart className={`w-4 h-4 ${wishlisted ? 'fill-red-500 text-red-500' : ''}`} />
                    }
                    {wishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
                  </Button>

                  {/* Includes */}
                  <div className="space-y-3 pt-4 border-t">
                    {[
                      `${course.duration} of content`,
                      `${totalLessons} lessons`,
                      `${course.projects.length} real-world projects`,
                      'Certificate of completion',
                      'Lifetime access',
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-600" />
                        {item}
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </motion.div>
    </>
  );
}